/**
 * FERP Portal — Visual Tabulator Cell Formatters
 *
 * Pure HTML/SVG string formatters for Tabulator cells.
 * Tabulator cells cannot render React — these return raw markup.
 *
 * All formatters follow the Tabulator `formatter` signature:
 *   (cell: CellComponent) => string
 *
 * Formatters:
 * - sparklineFormatter: Tiny SVG polyline from number[] cell value
 * - heatmapFormatter: Color-graded background from numeric value
 * - avatarStackFormatter: Overlapping SVG circles with initials
 */

// Tabulator cell component type — use any for cross-version compat
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CellComponent = any;

// ============================================================================
// SPARKLINE
// ============================================================================

/**
 * Sparkline — renders a tiny 80×24 SVG polyline from a number[] cell value.
 * Gracefully falls back to '–' for non-array or empty values.
 */
export function sparklineFormatter(cell: CellComponent): string {
  const raw = cell.getValue();
  if (!Array.isArray(raw) || raw.length === 0) {
    return '<span style="color:var(--mantine-color-dimmed)">–</span>';
  }

  const values = raw.map(Number).filter((n) => !isNaN(n));
  if (values.length === 0) {
    return '<span style="color:var(--mantine-color-dimmed)">–</span>';
  }

  const w = 80;
  const h = 24;
  const pad = 2;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const points = values
    .map((v, i) => {
      const x = pad + (i / (values.length - 1 || 1)) * (w - 2 * pad);
      const y = h - pad - ((v - min) / range) * (h - 2 * pad);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  return `<svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" style="vertical-align:middle"><polyline points="${points}" fill="none" stroke="var(--mantine-color-blue-5)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}

// ============================================================================
// HEATMAP
// ============================================================================

/**
 * Heatmap — colors a cell background based on a numeric value interpolated
 * between min and max bounds.
 *
 * Returns a factory function that takes min/max bounds and optional color pair.
 * Default colors: green-1 → red-1 (light, theme-friendly).
 */
export function heatmapFormatter(
  min: number,
  max: number,
  colors?: [string, string],
): (cell: CellComponent) => string {
  const lowColor = colors?.[0] ?? 'var(--mantine-color-green-1)';
  const highColor = colors?.[1] ?? 'var(--mantine-color-red-1)';

  return (cell: CellComponent): string => {
    const raw = cell.getValue();
    const value = Number(raw);
    if (raw == null || isNaN(value)) {
      return '<span style="color:var(--mantine-color-dimmed)">–</span>';
    }

    const range = max - min || 1;
    const ratio = Math.max(0, Math.min(1, (value - min) / range));

    // CSS color-mix for theme-aware interpolation
    const bg = `color-mix(in srgb, ${highColor} ${(ratio * 100).toFixed(0)}%, ${lowColor})`;

    return `<div style="background:${bg};padding:2px 6px;border-radius:4px;text-align:center;font-size:12px;font-weight:500">${value}</div>`;
  };
}

// ============================================================================
// AVATAR STACK
// ============================================================================

/**
 * Avatar stack — overlapping SVG circles with initials, plus "+N" overflow.
 * Cell value: string[] of names. Renders up to maxVisible circles.
 */
export function avatarStackFormatter(
  maxVisible: number = 3,
): (cell: CellComponent) => string {
  return (cell: CellComponent): string => {
    const raw = cell.getValue();
    if (!Array.isArray(raw) || raw.length === 0) {
      return '<span style="color:var(--mantine-color-dimmed)">–</span>';
    }

    const names = raw.map(String);
    const visible = names.slice(0, maxVisible);
    const overflow = names.length - maxVisible;

    const circleR = 12;
    const diameter = circleR * 2;
    const overlap = 8;
    const totalWidth =
      visible.length * diameter - (visible.length - 1) * overlap + (overflow > 0 ? diameter - overlap + 4 : 0);
    const h = diameter + 2;

    // Simple hash → hue for consistent avatar color per name
    const nameToHue = (name: string): number => {
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      return Math.abs(hash) % 360;
    };

    const circles = visible
      .map((name, i) => {
        const cx = circleR + i * (diameter - overlap);
        const cy = circleR + 1;
        const hue = nameToHue(name);
        const initial = name.charAt(0).toUpperCase();
        return `<circle cx="${cx}" cy="${cy}" r="${circleR}" fill="hsl(${hue},55%,65%)" stroke="var(--mantine-color-body)" stroke-width="2"/><text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="central" fill="white" font-size="10" font-weight="600">${initial}</text>`;
      })
      .join('');

    const overflowSvg =
      overflow > 0
        ? (() => {
            const cx = circleR + visible.length * (diameter - overlap);
            const cy = circleR + 1;
            return `<circle cx="${cx}" cy="${cy}" r="${circleR}" fill="var(--mantine-color-gray-4)" stroke="var(--mantine-color-body)" stroke-width="2"/><text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="central" fill="var(--mantine-color-dark-3)" font-size="9" font-weight="600">+${overflow}</text>`;
          })()
        : '';

    return `<svg width="${totalWidth}" height="${h}" viewBox="0 0 ${totalWidth} ${h}" style="vertical-align:middle">${circles}${overflowSvg}</svg>`;
  };
}

// ============================================================================
// JURISDICTION LEVEL
// ============================================================================

/**
 * Jurisdiction dot colors (Mantine CSS variables).
 * Levels sourced from known-regulatory-nodes config:
 *   federal=blue, state=violet, payer=yellow, accreditor=cyan, standard=indigo, local=orange, tenant=teal
 */
const JURISDICTION_COLORS: Record<string, string> = {
  federal: 'var(--mantine-color-blue-6)',
  state: 'var(--mantine-color-violet-6)',
  payer: 'var(--mantine-color-yellow-6)',
  accreditor: 'var(--mantine-color-cyan-6)',
  standard: 'var(--mantine-color-indigo-6)',
  local: 'var(--mantine-color-orange-6)',
  tenant: 'var(--mantine-color-teal-6)',
};

const JURISDICTION_LABELS: Record<string, string> = {
  federal: 'Federal',
  state: 'State',
  payer: 'Payer',
  accreditor: 'Accreditor',
  standard: 'Standard',
  local: 'Local',
  tenant: 'Tenant policy',
};

/**
 * jurisdictionFormatter — colored dot with optional override superscript.
 *
 * Cell value: mandateJurisdiction string from backend (via known-regulatory-nodes config):
 *   "federal" | "state" | "payer" | "accreditor" | "standard" | "local" | "tenant" | null
 * Row data: { mandateIsOverride?: boolean | null } — accessed via cell.getRow().getData()
 *
 * Renders:
 *   ● (blue)    = federal mandate (HIPAA, CMS CoP, OSHA)
 *   ●↑ (violet) = state mandate that overrides federal baseline (MN 144G > CMS CoP)
 *   ● (violet)  = state mandate (no override)
 *   ● (yellow)  = payer mandate (UCare Minnesota, Delta Dental)
 *   ● (cyan)    = accreditor mandate (Joint Commission)
 *   ● (indigo)  = professional standard (ADA Standards)
 *   ● (teal)    = tenant-internal policy
 *   – (gray)    = no mandate reference
 */
export function jurisdictionFormatter(cell: CellComponent): string {
  const value = cell.getValue() as string | null | undefined;
  if (!value) {
    return '<span style="color:var(--mantine-color-gray-5)" title="No mandate reference — task origin untracked">–</span>';
  }

  const color = JURISDICTION_COLORS[value] ?? 'var(--mantine-color-gray-5)';
  const rowData = cell.getRow().getData() as {
    mandateIsOverride?: boolean | null;
    mandateRef?: string | null;
    mandateSourceUrl?: string | null;
  };
  const isOverride = rowData?.mandateIsOverride === true;
  const mandateRef = rowData?.mandateRef ?? '';
  const sourceUrl = rowData?.mandateSourceUrl ?? '';
  const label = JURISDICTION_LABELS[value] ?? value;

  const tooltip = buildJurisdictionTooltip(value, isOverride, mandateRef);
  const overrideMark = isOverride
    ? `<sup style="font-size:9px;color:var(--mantine-color-gray-6);vertical-align:super;margin-left:1px">↑</sup>`
    : '';
  const sourceLink = sourceUrl
    ? `<a href="${sourceUrl}" target="_blank" rel="noopener noreferrer" title="View official regulation" style="color:var(--mantine-color-gray-6);font-size:10px;text-decoration:none;margin-left:2px;vertical-align:middle" onclick="event.stopPropagation()">↗</a>`
    : '';

  return `<span style="display:inline-flex;align-items:baseline;gap:0;white-space:nowrap" title="${tooltip}"><span style="color:${color};font-size:16px;line-height:1" aria-label="${label}">●</span>${overrideMark}${sourceLink}</span>`;
}

/**
 * jurisdictionTooltip — Tabulator tooltip function for the mandate jurisdiction column.
 *
 * Use as `tooltip: jurisdictionTooltip` in a Tabulator column definition.
 * Provides a human-readable explanation of the jurisdiction level.
 */
export function jurisdictionTooltip(cell: CellComponent): string {
  const value = cell.getValue() as string | null | undefined;
  const rowData = cell.getRow().getData() as {
    mandateIsOverride?: boolean | null;
    mandateRef?: string | null;
  };
  const isOverride = rowData?.mandateIsOverride === true;
  const mandateRef = rowData?.mandateRef ?? '';
  return buildJurisdictionTooltip(value ?? '', isOverride, mandateRef);
}

function buildJurisdictionTooltip(level: string, isOverride: boolean, mandateRef: string): string {
  const refSuffix = mandateRef ? ` [${mandateRef}]` : '';
  switch (level) {
    case 'federal':
      return `Federal mandate — required by federal law (e.g., HIPAA, CMS CoP, OSHA)${refSuffix}`;
    case 'state':
      return isOverride
        ? `State mandate — overrides federal baseline (more restrictive)${refSuffix}`
        : `State mandate — required by state statute${refSuffix}`;
    case 'payer':
      return `Payer mandate — required by insurance/managed care contract${refSuffix}`;
    case 'accreditor':
      return `Accreditor mandate — required by accreditation body (e.g., Joint Commission)${refSuffix}`;
    case 'standard':
      return `Professional standard — required by professional standards body (e.g., ADA)${refSuffix}`;
    case 'local':
      return `Local mandate — required by local ordinance${refSuffix}`;
    case 'tenant':
      return `Tenant policy — internal requirement, not externally mandated${refSuffix}`;
    default:
      return mandateRef
        ? `Mandate reference: ${mandateRef} (jurisdiction unknown)`
        : 'No mandate reference — task origin untracked';
  }
}

// ============================================================================
// CREATED BY SOURCE (DRR-130)
// ============================================================================

const SOURCE_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  RULE_ENGINE: { label: 'Rule', color: 'var(--mantine-color-blue-6)', icon: '⚖' },
  WORKFLOW_OPERATION: { label: 'Workflow', color: 'var(--mantine-color-violet-6)', icon: '⟳' },
  TENANT_DECISION: { label: 'Tenant', color: 'var(--mantine-color-orange-6)', icon: '✎' },
};

const SOURCE_TOOLTIPS: Record<string, string> = {
  RULE_ENGINE: 'Created by Zen Engine rule — regulatory mandate (DRR-130)',
  WORKFLOW_OPERATION: 'Created by workflow SpawnTasksFromGroups on operational self-loop',
  TENANT_DECISION: 'Created by tenant via API/UI — subject to ViolationCheck',
};

/**
 * createdBySourceFormatter — Renders a compact badge showing how the task was created.
 *
 * DRR-130 causality: RuleEngine (regulatory), WorkflowOperation (recurring), TenantDecision (user).
 */
export function createdBySourceFormatter(cell: CellComponent): string {
  const value = cell.getValue() as string | null | undefined;
  if (!value) {
    return '<span style="color:var(--mantine-color-gray-5)">–</span>';
  }
  const cfg = SOURCE_CONFIG[value];
  if (!cfg) {
    return `<span style="color:var(--mantine-color-gray-6)">${value}</span>`;
  }
  const tooltip = SOURCE_TOOLTIPS[value] ?? value;
  return `<span style="display:inline-flex;align-items:center;gap:3px;font-size:11px;padding:1px 6px;border-radius:4px;background:color-mix(in srgb, ${cfg.color} 12%, transparent);color:${cfg.color};white-space:nowrap" title="${tooltip}">${cfg.icon} ${cfg.label}</span>`;
}

// ============================================================================
// VIOLATION OUTCOME (DRR-130 Gap #6)
// ============================================================================

const VIOLATION_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  warn: { label: 'Warning', color: 'var(--mantine-color-yellow-7)', icon: '⚠' },
  deferred: { label: 'Review', color: 'var(--mantine-color-orange-7)', icon: '⏳' },
};

const VIOLATION_TOOLTIPS: Record<string, string> = {
  warn: 'Task conflicts with an active regulatory rule — flagged for review (DRR-130)',
  deferred: 'Violation check deferred — 24h post-event review required (DRR-130)',
};

/**
 * violationOutcomeFormatter — Renders a badge for tasks that triggered a violation check.
 *
 * Only shows for "warn" and "deferred" outcomes. "block" tasks never reach the table
 * (mutation fails). Null = no violation = no badge.
 */
export function violationOutcomeFormatter(cell: CellComponent): string {
  const value = cell.getValue() as string | null | undefined;
  if (!value) return '';
  const cfg = VIOLATION_CONFIG[value];
  if (!cfg) return '';
  const tooltip = VIOLATION_TOOLTIPS[value] ?? value;
  return `<span style="display:inline-flex;align-items:center;gap:3px;font-size:11px;padding:1px 6px;border-radius:4px;background:color-mix(in srgb, ${cfg.color} 12%, transparent);color:${cfg.color};white-space:nowrap" title="${tooltip}">${cfg.icon} ${cfg.label}</span>`;
}

// ============================================================================
// ACTOR NAME RESOLUTION
// ============================================================================

/**
 * Actor name formatter factory — resolves sequential actor IDs (e.g., "17_1")
 * to human-readable names via a closure-captured resolver.
 *
 * Pattern: same as heatmapFormatter(min, max) — factory returns pure formatter.
 */
export function actorNameFormatter(
  resolveActorName: (id: string | null | undefined) => string,
): (cell: CellComponent) => string {
  return (cell: CellComponent): string => {
    const value = cell.getValue() as string | null | undefined;
    if (!value) {
      return '<span style="color:var(--mantine-color-dimmed)">—</span>';
    }
    const name = resolveActorName(value);
    if (name !== value) {
      return `<span title="${value}">${name}</span>`;
    }
    return value;
  };
}

// ============================================================================
// CONSTELLATION ID RESOLUTION
// ============================================================================

/**
 * Constellation name formatter — resolves constellation instance IDs
 * (e.g., "17-constellation-003") to the resident name via the row's
 * subjectActorId field (constellation center = subject actor).
 *
 * Uses Tabulator row data access: cell.getRow().getData().subjectActorId
 */
export function constellationNameFormatter(
  resolveActorName: (id: string | null | undefined) => string,
): (cell: CellComponent) => string {
  return (cell: CellComponent): string => {
    const constellationId = cell.getValue() as string | null | undefined;
    if (!constellationId) {
      return '<span style="color:var(--mantine-color-dimmed)">—</span>';
    }

    // The constellation center is the subject actor on the same row
    const rowData = cell.getRow().getData();
    const subjectId = rowData?.subjectActorId as string | undefined;
    if (subjectId) {
      const name = resolveActorName(subjectId);
      if (name !== subjectId) {
        return `<span title="${constellationId}">${name}</span>`;
      }
    }
    return `<span title="${constellationId}">${constellationId}</span>`;
  };
}
